import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import Button from './ui/Button';
import { useLanguage } from '../context/LanguageContext';

const FirstLoginDisclaimer = ({ isVisible, onClose }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  
  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <motion.div
          className="glass max-w-lg w-full rounded-2xl p-5 md:p-8 border border-primary/30 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))' }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <div className="text-center mb-4 md:mb-6">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3 md:mb-4">
              <Icon name="Heart" size={24} color="#FFD700" />
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-1 md:mb-2">
              {isSpanish ? 'Bienvenido a Lumiveta' : 'Welcome to Lumiveta'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isSpanish ? 'Luz para tu camino' : 'Light for your path'}
            </p>
          </div>

          <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
            <div className="glass rounded-lg p-3 md:p-4">
              <div className="flex items-start gap-2 md:gap-3">
                <Icon name="Info" size={18} color="#FFD700" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground text-sm md:text-base mb-1">
                    {isSpanish ? 'Aviso Importante' : 'Important Disclaimer'}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? 'LUMIVETA™ es una herramienta basada en la fe proporcionada como parte de nuestra misión de APROVECHANDO LA IA PARA EL RESTO DE NOSOTROS™. Proporciona guía basada en la fe y aliento espiritual usando tecnología de IA. Nuestras respuestas estan fundamentadas en las escrituras cristianas y disenadas para ofrecer consuelo y reflexion.'
                      : 'LUMIVETA™ is a faith-based tool provided as part of our mission of LEVERAGING AI FOR THE REST OF US™. It provides faith-based guidance and spiritual encouragement using AI technology. Our responses are grounded in Christian scripture and designed to offer comfort and reflection.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="glass rounded-lg p-3 md:p-4 border-warning/30 border">
              <div className="flex items-start gap-2 md:gap-3">
                <Icon name="AlertTriangle" size={18} color="#FFD700" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground text-sm md:text-base mb-1">
                    {isSpanish ? 'No Sustituye la Atención Profesional' : 'Not a Substitute for Professional Care'}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? <>Esta aplicación <strong>no</strong> reemplaza la atención profesional de salud mental, consejos médicos o consejería. Si estás en crisis, llama al <strong className="text-primary">988</strong> (Línea de Crisis).</>
                      : <>This app is <strong>not</strong> a replacement for professional mental health care, medical advice, or counseling. If you're in crisis, call <strong className="text-primary">988</strong> (Crisis Lifeline).</>}
                  </p>
                </div>
              </div>
            </div>

            <div className="glass rounded-lg p-3 md:p-4">
              <div className="flex items-start gap-2 md:gap-3">
                <Icon name="Shield" size={18} color="#10B981" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground text-sm md:text-base mb-1">
                    {isSpanish ? 'Tu Privacidad Importa' : 'Your Privacy Matters'}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? 'Tus reflexiones y datos están encriptados y seguros. Nunca compartimos tu información con terceros.'
                      : 'Your reflections and data are encrypted and secure. We never share your information with third parties.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3: AI Discernment & Accuracy */}
            <div className="glass rounded-lg p-3 md:p-4">
              <div className="flex items-start gap-2 md:gap-3">
                <Icon name="Cpu" size={18} color="#FFD700" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground text-sm md:text-base mb-1">
                    {isSpanish ? 'Divulgación de Tecnología IA' : 'AI Technology Disclosure'}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? 'Lumiveta funciona con Inteligencia Artificial. Aunque está diseñado para profundidad teológica, la IA puede ocasionalmente producir resultados inexactos o inesperados (alucinaciones). Esta herramienta está destinada a complementar, no reemplazar, su estudio personal de la Sagrada Escritura. Se espera que los usuarios ejerzan discernimiento espiritual.'
                      : 'Lumiveta is powered by Artificial Intelligence. While designed for theological depth, AI may occasionally produce inaccurate or unexpected results (hallucinations). This tool is intended to supplement, not replace, your personal study of Holy Scripture. Users are expected to exercise spiritual discernment.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4: Limitation of Liability */}
            <div className="glass rounded-lg p-3 md:p-4 border-primary/30 border">
              <div className="flex items-start gap-2 md:gap-3">
                <Icon name="Scale" size={18} color="#FFD700" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-foreground text-sm md:text-base mb-1">
                    {isSpanish ? 'Acuerdo y Exoneración' : 'Agreement & Release'}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? 'LUMIVETA™ es propiedad y está operado por GO OUTDOORS LLC™. Al continuar, usted acepta eximir de responsabilidad a GO OUTDOORS LLC™ y sus afiliados por cualquier acción tomada o consecuencias derivadas del uso de esta aplicación. Usted acepta que cualquier responsabilidad total de GO OUTDOORS LLC™ derivada de su uso de este servicio está estrictamente limitada a $100.00 o el total de tarifas que nos haya pagado en los últimos doce meses, lo que sea mayor. Usted reconoce que es el único responsable de sus propias decisiones de vida, salud y bienestar.'
                      : 'LUMIVETA™ is owned and operated by GO OUTDOORS LLC™. By continuing, you agree to hold GO OUTDOORS LLC™ and its affiliates harmless for any actions taken or consequences arising from the use of this app. You agree that any total liability of GO OUTDOORS LLC™ arising from your use of this service is strictly limited to $100.00 or the total fees you have paid to us in the last twelve months, whichever is greater. You acknowledge that you are solely responsible for your own life choices, health, and well-being.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Button
            variant="default"
            fullWidth
            onClick={onClose}
            className="gold-glow"
            data-testid="disclaimer-continue-btn"
          >
            {isSpanish ? 'Entiendo - Continuar' : 'I Understand - Continue'}
          </Button>

          <p className="text-xs text-center text-muted-foreground mt-3 md:mt-4">
            {isSpanish 
              ? 'Al continuar, reconoces que has leído y entendido este aviso.'
              : 'By continuing, you acknowledge that you have read and understood this disclaimer.'}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FirstLoginDisclaimer;
